import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, updateProfile } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { getAge, isPlausibleDateOfBirth, MIN_SIGNUP_AGE } from '../utils/age';

export default function RedeemGift() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const giftId = searchParams.get('giftId');
  const { currentUser } = useAuth();

  const [gift, setGift] = useState(null);
  const [loadingGift, setLoadingGift] = useState(true);
  const [giftError, setGiftError] = useState('');

  // 'signup' (new account) | 'signin' (existing account, already has a
  // password) — a recipient who already has an account was previously stuck:
  // told to "sign in at /auth" with no way back to actually redeem.
  const [mode, setMode] = useState('signup');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [birthday, setBirthday] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (!giftId) {
      setGiftError('No gift ID provided.');
      setLoadingGift(false);
      return;
    }

    const fns = getFunctions(getApp());
    const getGiftDetails = httpsCallable(fns, 'getGiftDetails');

    getGiftDetails({ giftId })
      .then(result => {
        const data = result.data;
        if (data.status === 'redeemed') {
          setGiftError('This gift has already been redeemed.');
        } else if (data.status === 'pending') {
          setGiftError('This gift is still being processed. Please try again in a few minutes.');
        } else {
          setGift(data);
          if (data.recipientEmail) setEmail(data.recipientEmail);
          if (data.recipientName) setName(data.recipientName);
        }
      })
      .catch(err => {
        setGiftError(err.message || 'Could not load gift details.');
      })
      .finally(() => setLoadingGift(false));
  }, [giftId]);

  // Shared by all three entry points (new signup, sign-in, already signed
  // in) — calls the redeemGift callable for whoever is currently
  // authenticated and moves on to onboarding.
  const redeemForCurrentUser = async () => {
    const fns = getFunctions(getApp());
    const redeemGift = httpsCallable(fns, 'redeemGift');
    await redeemGift({ giftId });
    navigate('/onboard?gift=true');
  };

  const handleRedeem = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || password.length < 6) {
      setFormError('Please fill in all fields. Password must be at least 6 characters.');
      return;
    }
    if (!isPlausibleDateOfBirth(birthday)) {
      setFormError('Please enter a valid date of birth.');
      return;
    }
    if (getAge(birthday) < MIN_SIGNUP_AGE) {
      setFormError(`You must be at least ${MIN_SIGNUP_AGE} years old to create an account.`);
      return;
    }

    setSubmitting(true);
    setFormError('');

    try {
      const auth = getAuth();
      const { user } = await createUserWithEmailAndPassword(auth, email.trim(), password);
      await updateProfile(user, { displayName: name.trim() });

      // Create user doc in Firestore
      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        email: email.trim().toLowerCase(),
        displayName: name.trim(),
        firstName: name.trim().split(' ')[0],
        birthday,
        createdAt: serverTimestamp(),
        familyIds: [],
      });

      await redeemForCurrentUser();
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        setFormError('An account with that email already exists — sign in below to redeem your gift.');
        setMode('signin');
      } else {
        setFormError(err.message || 'Something went wrong. Please try again.');
      }
      setSubmitting(false);
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setFormError('Please enter your email and password.');
      return;
    }

    setSubmitting(true);
    setFormError('');

    try {
      const auth = getAuth();
      await signInWithEmailAndPassword(auth, email.trim(), password);
      await redeemForCurrentUser();
    } catch (err) {
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password' || err.code === 'auth/user-not-found') {
        setFormError('Incorrect email or password.');
      } else {
        setFormError(err.message || 'Something went wrong. Please try again.');
      }
      setSubmitting(false);
    }
  };

  const handleRedeemAsCurrentUser = async () => {
    setSubmitting(true);
    setFormError('');
    try {
      await redeemForCurrentUser();
    } catch (err) {
      setFormError(err.message || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  if (loadingGift) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-space-cadet via-space-cadet/90 to-cerulean/80 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white" />
      </div>
    );
  }

  if (giftError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-space-cadet via-space-cadet/90 to-cerulean/80 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-space-cadet mb-2">Couldn't Load Gift</h2>
          <p className="text-gray-500 text-sm">{giftError}</p>
          <a href="mailto:hello@familybibleapp.com" className="mt-4 inline-block text-cerulean text-sm underline">
            Contact support
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-space-cadet via-space-cadet/90 to-cerulean/80 pb-16">
      <div className="max-w-md mx-auto px-4 pt-10">

        {/* Gift card */}
        <div className="bg-gradient-to-r from-cerulean to-aqua-deep rounded-2xl p-6 mb-6 text-white shadow-lg">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
              </svg>
            </div>
            <span className="text-sm font-medium opacity-80">You've received a gift!</span>
          </div>
          <h1 className="text-2xl font-bold mb-1">1 Year of {gift?.planName}</h1>
          <p className="text-white/80 text-sm">
            From <strong>{gift?.giverName || 'a generous person'}</strong> — already paid for you.
          </p>
          {gift?.message && (
            <p className="mt-3 text-sm italic text-white/90 border-l-2 border-white/40 pl-3">
              "{gift.message}"
            </p>
          )}
        </div>

        {currentUser ? (
          /* Already signed in — redeem directly, no form needed */
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <h2 className="text-xl font-bold text-space-cadet mb-1">You're signed in</h2>
            <p className="text-gray-500 text-sm mb-5">
              Signed in as <strong>{currentUser.email}</strong>.
            </p>

            {gift?.recipientEmail && currentUser.email?.toLowerCase().trim() !== gift.recipientEmail ? (
              <div className="p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl text-sm space-y-2">
                <p>This gift was sent to <strong>{gift.recipientEmail}</strong>, a different email address.</p>
                <button
                  type="button"
                  onClick={() => signOut(getAuth())}
                  className="text-cerulean underline font-medium"
                >
                  Sign out and use the correct account
                </button>
              </div>
            ) : (
              <>
                {formError && (
                  <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm mb-4">
                    {formError}
                  </div>
                )}
                <button
                  type="button"
                  onClick={handleRedeemAsCurrentUser}
                  disabled={submitting}
                  className="w-full bg-gradient-to-r from-cerulean to-aqua-deep text-white font-semibold py-3.5 rounded-xl hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Redeeming…</>
                  ) : (
                    'Redeem My Gift'
                  )}
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-xl p-6">
            {mode === 'signup' ? (
              <>
                <h2 className="text-xl font-bold text-space-cadet mb-1">Create your account</h2>
                <p className="text-gray-500 text-sm mb-5">
                  Your first year is paid for — just set up your account below.
                </p>

                <form onSubmit={handleRedeem} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
                    <input
                      type="text"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      placeholder="Your full name"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cerulean focus:border-transparent outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                    <input
                      type="date"
                      value={birthday}
                      onChange={e => setBirthday(e.target.value)}
                      required
                      max={new Date().toISOString().slice(0, 10)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cerulean focus:border-transparent outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                    <input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cerulean focus:border-transparent outline-none"
                    />
                    {gift?.recipientEmail && email !== gift.recipientEmail && (
                      <p className="text-xs text-amber-600 mt-1">
                        Note: this gift was sent to {gift.recipientEmail}. Please use that email to redeem.
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Choose a Password</label>
                    <input
                      type="password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="At least 6 characters"
                      required
                      minLength={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cerulean focus:border-transparent outline-none"
                    />
                  </div>

                  {formError && (
                    <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">
                      {formError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-gradient-to-r from-cerulean to-aqua-deep text-white font-semibold py-3.5 rounded-xl hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Creating account…</>
                    ) : (
                      'Redeem My Gift & Create Account'
                    )}
                  </button>
                </form>

                <p className="text-center text-xs text-gray-400 mt-4">
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => { setMode('signin'); setFormError(''); setPassword(''); }}
                    className="text-cerulean underline"
                  >
                    Sign in here
                  </button>
                  {' '}to redeem your gift.
                </p>
              </>
            ) : (
              <>
                <h2 className="text-xl font-bold text-space-cadet mb-1">Sign in to redeem</h2>
                <p className="text-gray-500 text-sm mb-5">
                  Sign in with your existing account to apply this gift.
                </p>

                <form onSubmit={handleSignIn} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                    <input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cerulean focus:border-transparent outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                    <input
                      type="password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Your password"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cerulean focus:border-transparent outline-none"
                    />
                  </div>

                  {formError && (
                    <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">
                      {formError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-gradient-to-r from-cerulean to-aqua-deep text-white font-semibold py-3.5 rounded-xl hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Signing in…</>
                    ) : (
                      'Sign In & Redeem My Gift'
                    )}
                  </button>
                </form>

                <p className="text-center text-xs text-gray-400 mt-4">
                  New here?{' '}
                  <button
                    type="button"
                    onClick={() => { setMode('signup'); setFormError(''); setPassword(''); }}
                    className="text-cerulean underline"
                  >
                    Create an account instead
                  </button>
                </p>
              </>
            )}
          </div>
        )}

        <p className="text-center text-white/50 text-xs mt-6">
          Questions? <a href="mailto:hello@familybibleapp.com" className="underline hover:text-white/80">hello@familybibleapp.com</a>
        </p>
      </div>
    </div>
  );
}
